import { useAuthStore } from '../stores/auth.js'

// Performance optimization: Response caching and request deduplication
class ShiftApiCache {
  constructor() {
    this.cache = new Map()
    this.pendingRequests = new Map()
    this.maxAge = 5 * 60 * 1000 // 5 minutes
    this.maxSize = 100
  }

  getCacheKey(url, options = {}) {
    return `${options.method || 'GET'}:${url}:${JSON.stringify(options.body || {})}`
  }

  get(url, options) {
    const key = this.getCacheKey(url, options)
    const cached = this.cache.get(key)
    
    if (cached && Date.now() - cached.timestamp < this.maxAge) {
      return cached.data
    }
    return null
  }

  set(url, options, data) {
    const key = this.getCacheKey(url, options)
    
    // Implement LRU eviction if cache is full
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value
      this.cache.delete(firstKey)
    }
    
    this.cache.set(key, {
      data: data,
      timestamp: Date.now()
    })
  }

  // Get or create pending request to avoid duplicates
  getOrCreateRequest(url, options, requestFn) {
    const key = this.getCacheKey(url, options)
    
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)
    }
    
    const promise = requestFn().finally(() => {
      this.pendingRequests.delete(key)
    })
    
    this.pendingRequests.set(key, promise)
    return promise
  }

  invalidate(pattern) {
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key)
      }
    }
  }
}

const apiCache = new ShiftApiCache()

export const shiftApi = {
  // ========== SHIFT MANAGEMENT ==========
  
  // Get all shifts (cached)
  async getAllShifts() {
    const url = '/api/shifts/'
    const cached = apiCache.get(url)
    if (cached) return cached

    return apiCache.getOrCreateRequest(url, {}, async () => {
      const authStore = useAuthStore()
      const response = await authStore.makeAuthenticatedRequest(url)
      const data = await response.json()
      apiCache.set(url, {}, data)
      return data
    })
  },

  // Get shifts for a specific date range (cached)
  async getShiftsInRange(startDate, endDate) {
    const start = startDate.toISOString().split('T')[0]
    const end = endDate.toISOString().split('T')[0]
    const url = `/api/shifts/?start_date=${start}&end_date=${end}`
    
    const cached = apiCache.get(url)
    if (cached) return cached

    return apiCache.getOrCreateRequest(url, {}, async () => {
      const authStore = useAuthStore()
      const response = await authStore.makeAuthenticatedRequest(url)
      const data = await response.json()
      apiCache.set(url, {}, data)
      return data
    })
  },

  // Get a specific shift by ID (cached)
  async getShift(shiftId) {
    const url = `/api/shifts/${shiftId}/`
    const cached = apiCache.get(url)
    if (cached) return cached

    return apiCache.getOrCreateRequest(url, {}, async () => {
      const authStore = useAuthStore()
      const response = await authStore.makeAuthenticatedRequest(url)
      const data = await response.json()
      apiCache.set(url, {}, data)
      return data
    })
  },

  // Create a new shift (invalidates cache)
  async createShift(shiftData) {
    const authStore = useAuthStore()
    const response = await authStore.makeAuthenticatedRequest(`/api/shifts/`, {
      method: 'POST',
      body: JSON.stringify({
        start_time: shiftData.start_time,
        end_time: shiftData.end_time,
        created_by: shiftData.created_by || authStore.user?.id
      })
    })
    const result = await response.json()
    
    // Invalidate relevant cache entries
    apiCache.invalidate('/api/shifts')
    apiCache.invalidate('/api/shift-assignments')
    
    return result
  },

  // Update an existing shift (invalidates cache)
  async updateShift(shiftId, shiftData) {
    const authStore = useAuthStore()
    const response = await authStore.makeAuthenticatedRequest(`/api/shifts/${shiftId}/`, {
      method: 'PUT',
      body: JSON.stringify(shiftData)
    })
    const result = await response.json()
    
    // Invalidate relevant cache entries
    apiCache.invalidate('/api/shifts')
    apiCache.invalidate('/api/shift-assignments')
    
    return result
  },

  // Delete a shift (invalidates cache)
  async deleteShift(shiftId) {
    const authStore = useAuthStore()
    const response = await authStore.makeAuthenticatedRequest(`/api/shifts/${shiftId}/`, {
      method: 'DELETE'
    })
    const result = response.status === 204 ? null : await response.json()
    
    // Invalidate relevant cache entries
    apiCache.invalidate('/api/shifts')
    apiCache.invalidate('/api/shift-assignments')
    
    return result
  },

  // ========== SHIFT ASSIGNMENT MANAGEMENT ==========

  // Get all shift assignments (cached)
  async getAllAssignments() {
    const url = `/api/shift-assignments/`
    const cached = apiCache.get(url)
    if (cached) return cached

    return apiCache.getOrCreateRequest(url, {}, async () => {
      const authStore = useAuthStore()
      const response = await authStore.makeAuthenticatedRequest(url)
      const data = await response.json()
      apiCache.set(url, {}, data)
      return data
    })
  },

  // Get assignments for a specific shift (cached)
  async getShiftAssignments(shiftId) {
    const url = `/api/shift-assignments/?shift=${shiftId}`
    const cached = apiCache.get(url)
    if (cached) return cached

    return apiCache.getOrCreateRequest(url, {}, async () => {
      const authStore = useAuthStore()
      const response = await authStore.makeAuthenticatedRequest(url)
      const data = await response.json()
      apiCache.set(url, {}, data)
      return data
    })
  },

  // Get assignments for a specific worker (cached)
  async getWorkerAssignments(workerId) {
    const url = `/api/shift-assignments/?worker=${workerId}`
    const cached = apiCache.get(url)
    if (cached) return cached

    return apiCache.getOrCreateRequest(url, {}, async () => {
      const authStore = useAuthStore()
      const response = await authStore.makeAuthenticatedRequest(url)
      const data = await response.json()
      apiCache.set(url, {}, data)
      return data
    })
  },

  // Create a new shift assignment (invalidates cache)
  async assignWorkerToShift(shiftId, workerId, status = 'approved') {
    const authStore = useAuthStore()
    
    const payload = {
      shift_id: shiftId,
      worker_id: workerId,
      status: status
    }

    const response = await authStore.makeAuthenticatedRequest(`/api/shift-assignments/`, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
    
    const result = await response.json()
    
    // Invalidate relevant cache entries
    apiCache.invalidate('/api/shift-assignments')
    
    return result
  },

  // Remove worker from shift (invalidates cache)
  async removeWorkerFromShift(assignmentId) {
    const authStore = useAuthStore()
    const response = await authStore.makeAuthenticatedRequest(`/api/shift-assignments/${assignmentId}/`, {
      method: 'DELETE'
    })
    const result = response.status === 204 ? null : await response.json()
    
    // Invalidate relevant cache entries
    apiCache.invalidate('/api/shift-assignments')
    
    return result
  },

  // Update assignment details (invalidates cache)
  async updateAssignmentDetails(assignmentId, updates) {
    const authStore = useAuthStore()
    const response = await authStore.makeAuthenticatedRequest(`/api/shift-assignments/${assignmentId}/`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    })
    const result = await response.json()
    
    // Invalidate relevant cache entries
    apiCache.invalidate('/api/shift-assignments')
    
    return result
  },

  // Request worker approval for a shift within 14 days (invalidates cache)
  async requestWorkerApproval(shiftId, workerId) {
    const authStore = useAuthStore();
    const response = await authStore.makeAuthenticatedRequest(`/api/shift-assignments/approve/`, {
      method: 'PATCH',
      body: JSON.stringify({
        shift: shiftId,
        worker: workerId,
        approval_required: true
      })
    });
    const result = await response.json();
    
    // Invalidate relevant cache entries
    apiCache.invalidate('/api/shift-assignments')
    
    return result;
  },

  // ========== COMBINED DATA OPERATIONS ==========

  // Get shifts with their assignments for a date range (optimized with caching)
  async getShiftsWithAssignments(startDate, endDate) {
    const [shifts, assignments] = await Promise.all([
      this.getShiftsInRange(startDate, endDate),
      this.getAllAssignments()
    ])

    // The backend now includes worker details in assignments
    // assignment.worker could be either an ID (old) or an object with details (new)
    // Check if we need to fetch worker details
    const firstAssignment = assignments[0]
    const workerIsObject = firstAssignment && typeof firstAssignment.worker === 'object' && firstAssignment.worker !== null
    
    let workerMap = new Map() // Use Map for O(1) lookups
    
    if (workerIsObject) {
      // Worker details already included in assignments
      assignments.forEach(assignment => {
        if (assignment.worker && assignment.worker.id) {
          workerMap.set(assignment.worker.id, assignment.worker)
        }
      })
    } else {
      // Worker is just an ID, need to fetch details with optimized batching
      const workerIds = [...new Set(assignments.map(assignment => assignment.worker).filter(Boolean))]
      
      if (workerIds.length > 0) {
        // Batch worker requests with caching
        const workers = await Promise.all(
          workerIds.map(workerId => this.getCachedWorker(workerId))
        )
        workers.forEach(worker => {
          if (worker) {
            workerMap.set(worker.id, worker)
          }
        })
      }
    }

    // Filter assignments by date range for better performance
    const relevantShiftIds = new Set(shifts.map(shift => shift.id))
    const filteredAssignments = assignments.filter(assignment => {
      const shiftId = typeof assignment.shift === 'object' ? assignment.shift.id : assignment.shift
      return relevantShiftIds.has(shiftId)
    })

    // Combine shifts with their assignments and worker details using optimized lookup
    return shifts.map(shift => ({
      ...shift,
      assignments: filteredAssignments
        .filter(assignment => {
          const shiftId = typeof assignment.shift === 'object' ? assignment.shift.id : assignment.shift
          return shiftId === shift.id
        })
        .map(assignment => {
          const workerId = typeof assignment.worker === 'object' ? assignment.worker.id : assignment.worker
          return {
            ...assignment,
            worker: workerMap.get(workerId) || null
          }
        })
    }))
  },

  // Helper method for cached worker fetching
  async getCachedWorker(workerId) {
    const url = `/api/users/${workerId}/`
    const cached = apiCache.get(url)
    if (cached) return cached

    return apiCache.getOrCreateRequest(url, {}, async () => {
      const authStore = useAuthStore()
      try {
        const response = await authStore.makeAuthenticatedRequest(url)
        if (response.ok) {
          const data = await response.json()
          apiCache.set(url, {}, data)
          return data
        }
      } catch (e) {
        console.warn(`Failed to fetch worker ${workerId}:`, e)
      }
      return null
    })
  },

  // Get worker's shifts with assignment status for a date range (cached)
  async getWorkerSchedule(workerId, startDate, endDate) {
    // Use the efficient backend-filtered API with caching
    const assignments = await this.getWorkerShiftsInRange(workerId, startDate, endDate)
    
    // Map assignments to shift format with assignment data (optimized)
    const shifts = assignments
      .map(assignment => {
        const shift = assignment.shift_details || assignment.shift
        
        if (!shift || typeof shift !== 'object') {
          console.warn('Invalid shift in assignment:', assignment)
          return null
        }
        
        return {
          ...shift,
          assignment: assignment
        }
      })
      .filter(Boolean) // More efficient than checking for null
    
    return shifts
  },

  // Get worker's assigned shifts in a date range (cached, backend-filtered)
  async getWorkerShiftsInRange(workerId, startDate, endDate) {
    // Format dates in local timezone (YYYY-MM-DD) to avoid timezone conversion issues
    const start = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`
    const end = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`
    
    const url = `/api/shift-assignments/?worker=${workerId}&start_date=${start}&end_date=${end}`
    const cached = apiCache.get(url)
    if (cached) return cached

    return apiCache.getOrCreateRequest(url, {}, async () => {
      const authStore = useAuthStore()
      const response = await authStore.makeAuthenticatedRequest(url)
      const data = await response.json()
      apiCache.set(url, {}, data)
      return data
    })
  },

  // ========== CONFLICT DETECTION ==========

  // Check if worker has conflicting shifts (optimized with parallel requests)
  async checkWorkerConflicts(workerId, newStartTime, newEndTime, excludeShiftId = null) {
    const workerAssignments = await this.getWorkerAssignments(workerId)
    
    // Filter and batch shift requests more efficiently
    const relevantAssignments = workerAssignments
      .filter(assignment => assignment.shift !== excludeShiftId) // Exclude current shift if editing
    
    if (relevantAssignments.length === 0) {
      return { hasConflicts: false, conflicts: [] }
    }
    
    // Get shift details for each assignment with optimized parallel requests
    const existingShifts = await Promise.all(
      relevantAssignments.map(assignment => this.getShift(assignment.shift))
    )
    
    const newStart = new Date(newStartTime)
    const newEnd = new Date(newEndTime)
    
    // Check for overlaps with optimized algorithm
    const conflicts = existingShifts.filter(shift => {
      if (!shift) return false
      
      const shiftStart = new Date(shift.start_time)
      const shiftEnd = new Date(shift.end_time)
      
      // Check if there's any overlap (optimized comparison)
      return (newStart < shiftEnd && newEnd > shiftStart)
    })
    
    return {
      hasConflicts: conflicts.length > 0,
      conflicts: conflicts
    }
  },

  // Clear all cache (useful for force refresh)
  clearCache() {
    apiCache.cache.clear()
    apiCache.pendingRequests.clear()
  }
}